import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus } from "lucide-react";
import api from "@/lib/api";
import { formatINR } from "@/lib/utils-format";

const TIER_RANK = {
    "Essential": 1,
    "Essential CROS": 1,
    "Standard": 2,
    "Standard CROS": 2,
    "Advanced": 3,
    "Advanced CROS": 3,
    "Premium": 4,
    "Premium CROS": 4,
    "Flagship": 5,
    "Flagship CROS": 5,
};

const ROWS = [
    { key: "tier", label: "Technology Tier" },
    { key: "noise", label: "Speech-in-Noise Performance" },
    { key: "channels", label: "Sound Channels" },
    { key: "bluetooth", label: "Bluetooth Streaming" },
    { key: "rechargeable", label: "Rechargeable" },
    { key: "app", label: "Signia App Personalization" },
    { key: "cros", label: "CROS Support (Single-Sided Loss)" },
    { key: "warranty", label: "Warranty" },
    { key: "ideal", label: "Ideal For" },
    { key: "price", label: "Price (per pair)" },
    { key: "emi", label: "No-Cost EMI / month" },
];

const tierStars = (tier) => {
    const r = TIER_RANK[tier] || 1;
    return "●".repeat(r) + "○".repeat(5 - r);
};

const CHANNELS_BY_TIER = {
    "Essential": 16,
    "Essential CROS": 16,
    "Standard": 24,
    "Standard CROS": 24,
    "Advanced": 32,
    "Advanced CROS": 32,
    "Premium": 36,
    "Premium CROS": 36,
    "Flagship": 48,
    "Flagship CROS": 48,
};

const cellFor = (key, p) => {
    switch (key) {
        case "tier":
            return <span className="font-semibold text-[#003E4D]">{tierStars(p.technology_level)}</span>;
        case "noise":
            return <span>{tierStars(p.technology_level)}</span>;
        case "channels":
            return <span>{CHANNELS_BY_TIER[p.technology_level] || 16} channels</span>;
        case "bluetooth":
        case "rechargeable":
        case "app":
            return <Check size={16} className="text-[#003E4D]" />;
        case "cros":
            return p.is_cros ? <Check size={16} className="text-[#003E4D]" /> : <Minus size={16} className="text-[#9CA3AF]" />;
        case "warranty": {
            const s = (p.slug || "").toLowerCase();
            const t = (p.technology_level || "").toLowerCase();
            const yrs = (s.includes("5ix") || s.includes("7ix") || t.includes("premium") || t.includes("flagship")) ? 4 : 3;
            return <span className="font-medium">{yrs}-year Signia India</span>;
        }
        case "ideal":
            return <span className="text-[13px]">{p.suitable_for}</span>;
        case "price":
            return <span className="font-semibold">{formatINR(p.price)}</span>;
        case "emi":
            return <span>{p.emi_per_month ? formatINR(p.emi_per_month) : "—"}</span>;
        default:
            return null;
    }
};

export default function CompareTable({ limit, filter = "non-cros" }) {
    const [items, setItems] = useState([]);
    useEffect(() => {
        api.get("/products").then((r) => setItems(r.data)).catch(() => {});
    }, []);
    // Canonical order: 1IX → 2IX → 3IX → 5IX → 7IX (falls back gracefully if a slug is missing)
    const NON_CROS_ORDER = [
        ["signia-styletto-1ix", "kit-styletto-1ix", "signia-kit-styletto-1ix"],
        ["kit-styletto-2ix"],
        ["kit-styletto-3ix"],
        ["kit-styletto-5ix", "kit-styletto-4ix"],  // 4IX fills in if 5IX missing
        ["kit-styletto-7ix"],
    ];
    let filtered = items;
    if (filter === "non-cros") {
        filtered = NON_CROS_ORDER
            .map((slugs) => slugs.map((s) => items.find((p) => p.slug === s)).find(Boolean))
            .filter(Boolean);
    } else if (filter === "cros") {
        filtered = items.filter((p) => p.is_cros);
    }
    const list = limit ? filtered.slice(0, limit) : filtered;

    return (
        <section className="section bg-[#f8f9fa]" data-testid="compare-table-section">
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div className="max-w-[640px]">
                        <p className="eyebrow">Compare Models</p>
                        <h2 className="font-serif text-[40px] md:text-[56px] leading-[1.04] mt-3">
                            Find your perfect <span className="italic text-[#003E4D]">technology tier.</span>
                        </h2>
                    </div>
                    <Link to="/compare" className="btn-secondary self-start" data-testid="view-full-compare-link">Full comparison</Link>
                </div>

                <div className="overflow-x-auto bg-white rounded-3xl border border-black/5 no-scrollbar">
                    <table className="w-full min-w-[720px] text-left" data-testid="compare-table">
                        <thead>
                            <tr className="border-b border-[#E5E7EB]">
                                <th className="p-5 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#4a5568] w-[220px]">Feature</th>
                                {list.map((p) => (
                                    <th key={p.slug} className="p-5 text-left">
                                        <div className="font-serif text-[20px] text-[#1a1a1a]">{p.short_name}</div>
                                        <div className="text-[12px] text-[#4a5568] font-normal mt-0.5">{p.technology_level}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {ROWS.map((r) => (
                                <tr key={r.key} className="border-b border-[#E5E7EB] last:border-0">
                                    <td className="p-5 text-[13.5px] text-[#4a5568] font-medium">{r.label}</td>
                                    {list.map((p) => (
                                        <td key={p.slug} className="p-5 text-[14px] text-[#1a1a1a]">
                                            {cellFor(r.key, p)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}
